import React, { useMemo } from "react";
import SearchableTable from "./SearchableTable";
import { createPostgRESTDataAdapter } from "./dataAdapters";

/**
 * PostgRESTTable - Convenience wrapper for PostgREST-backed tables
 *
 * Provides a simplified API for tables backed by PostgREST endpoints.
 * All SearchableTable props are supported via pass-through.
 *
 * @param {string} endpoint - PostgREST server URL
 * @param {string} resource - Table/view/RPC name
 * @param {object} columns - Column definitions
 * @param {array|string} searchColumns - Columns to include in search
 * @param {object} headers - HTTP headers (e.g., Authorization)
 * @param {object} staticFilters - Always-applied filters
 * @param {function} renderActions - Optional function to render actions column: (row) => ReactNode
 * @param {object} ...tableProps - All other SearchableTable props
 *
 * @example
 * <PostgRESTTable
 *   endpoint="https://api.example.com/rest/v1"
 *   resource="products"
 *   columns={{ id: { type: "number" }, name: { type: "string" } }}
 *   searchColumns={["name", "description"]}
 *   staticFilters={{ "is_active": "eq.true" }}
 *   headers={{ Authorization: "Bearer ..." }}
 *   pageSize={20}
 *   defaultSort={{ created_at: "desc" }}
 *   toolbar={<CreateButton />}
 *   renderActions={(row) => (
 *     <div className="d-flex gap-1">
 *       <button onClick={() => handleEdit(row)}>Edit</button>
 *       <button onClick={() => handleDelete(row)}>Delete</button>
 *     </div>
 *   )}
 * />
 */
export default function PostgRESTTable({
  endpoint,
  resource,
  columns = {},
  searchColumns = [],
  headers = {},
  staticFilters = {},
  ...tableProps
}) {
  const dataAdapter = useMemo(
    () =>
      createPostgRESTDataAdapter({
        endpoint,
        resource,
        headers,
        staticFilters,
        columns,
        searchColumns,
      }),
    [endpoint, resource, headers, staticFilters, columns, searchColumns],
  );

  return (
    <SearchableTable
      columns={columns}
      searchColumns={searchColumns}
      dataAdapter={dataAdapter}
      pageSize={tableProps.pageSize}
      renderCell={tableProps.renderCell}
      renderActions={tableProps.renderActions}
      rowClassName={tableProps.rowClassName}
      defaultSort={tableProps.defaultSort}
      refreshTrigger={tableProps.refreshTrigger}
      toolbar={tableProps.toolbar}
      enablePagination={tableProps.enablePagination}
      enableSearch={tableProps.enableSearch}
      enableFiltering={tableProps.enableFiltering}
      enableSorting={tableProps.enableSorting}
    />
  );
}
